import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import useVendors from "../../../services/Inventory/useVendors";
import { GlobalContext } from "../../../Context/GlobalContext";
import NewPurchaseItemModel from "../NewPurchaseItem.Model";
import NoIndex from "../../../components/NoIndex";

const CreatePurchase = () => {
  const { userID } = useContext(GlobalContext);
  const navigate = useNavigate();
  const { state } = useLocation();
  const vendor = state?.vendor;
  const vendorId = vendor?.vendorId || vendor?.id;
  const storageKey = `purchase-items-${userID}-${vendorId}`;

  const { getItemDetails, createPurchase, loading } = useVendors();

  const itemIdRef = useRef(null);
  const [itemId, setItemId] = useState("");
  const [items, setItems] = useState([]);
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [newBarcode, setNewBarcode] = useState("");
  const [showClearModal, setShowClearModal] = useState(false);

  useEffect(() => {
    itemIdRef.current?.focus();
  }, []);

  
 const fetchItemDetails = async () => {
  const scannedValue = itemId.trim();
  if (!scannedValue) return;

  const existingIndex = items.findIndex(
    (i) =>
      String(i.barcodeNumber || "").trim() === String(scannedValue).trim() ||
      String(i.item_id || "").trim() === String(scannedValue).trim() ||
      String(i.id || "").trim() === String(scannedValue).trim()
  );

  if (existingIndex !== -1) {
    const updated = [...items];
    updated[existingIndex] = {
      ...updated[existingIndex],
      quantity: (parseInt(updated[existingIndex].quantity) || 0) + 1,
    };

    setItems(updated);
    setItemId("");
    return;
  }

const res = await getItemDetails(scannedValue);

console.log("Barcode API response:", res);

const itemData =
  res?.response ||
  res?.data?.response ||
  res?.data?.data ||
  res?.data ||
  res ||
  null;

const isSuccess =
  res?.responseCode === "00" ||
  res?.data?.responseCode === "00" ||
  itemData?.item_id ||
  itemData?.id ||
  itemData?.item_name;

if (isSuccess && itemData && typeof itemData === "object") {
  const newItem = {
    ...itemData,
    quantity: 1,
    purchasePrice: String(itemData.price || itemData.purchasePrice || "0"),
    barcodeNumber: itemData.barcodeNumber || scannedValue,
    item_id: itemData.item_id || itemData.id,
  };

  setItems((prev) => [...prev, newItem]);
  setItemId("");
  setShowNotFoundModal(false);
} else {
  setNewBarcode(scannedValue);
  setShowNotFoundModal(true);
}
 }

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];

      if (field === "quantity") {
        updated[index] = {
          ...updated[index],
          quantity: Math.max(1, parseInt(value) || 1),
        };
      } else if (field === "purchasePrice") {
        updated[index] = {
          ...updated[index],
          purchasePrice: value,
        };
      } else {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }

      return updated;
    });
  };

  const handleRemoveItem = (index) => {
    const updated = [...items];
    updated.splice(index, 1);
    setItems(updated);
    toast.success("Item removed");
  };

  const handleCreatePurchase = async () => {
    if (items.length === 0) {
      toast.warning("Please add at least one item");
      return;
    }
    const result = await createPurchase(vendorId, items);
    if (result?.responseCode === "00") {
      localStorage.removeItem(storageKey);
      navigate("/inventory/vendors");
    }
  };

  const handleRemoveAll = () => {
    if (items.length === 0) {
      toast.info("No items to remove");
      return;
    }

    setShowClearModal(true);
  };

  const confirmClearAll = () => {
    setItems([]);
    setShowClearModal(false);
    toast.success("All items cleared");
  };

  const cancelClearAll = () => {
    setShowClearModal(false);
  };

  const totalQty = items.reduce((s, i) => s + parseInt(i.quantity || 0), 0);
  const totalAmount = items.reduce((sum, item) => {
    const qty = parseInt(item.quantity) || 0;
    const price = parseFloat(item.purchasePrice) || 0;
    return sum + qty * price;
  }, 0);

  const isLoaded = useRef(false);

  useEffect(() => {
    if (!vendorId || !userID) return;

    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setItems(parsed);
      } catch (err) {
        setItems([]);
      }
    }

    isLoaded.current = true;
  }, [userID, vendorId]);

  useEffect(() => {
    if (!vendorId || !userID) return;
    if (!isLoaded.current) return;

    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items, storageKey]);

  return (
    <>
    <ToastContainer/>
   
    <div className="min-h-screen p-4 md:p-6 space-y-4 montserrat-font-1">
      <NoIndex/>

      {/* ── Header ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm montserrat-font-1 px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-800 montserrat-font-1">Create Purchase</h2>
          <p className="text-sm text-gray-500 montserrat-font-2">
            Vendor:{" "}
            <span className="font-semibold text-gray-700">
              {vendor?.vendorName}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2 montserrat-font-1">
          <button
            onClick={() => navigate("/Inventory/Vendors")}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleCreatePurchase}
            disabled={loading || items.length === 0}
            className="px-5 py-2 text-sm font-semibold text-white bg-green-500 rounded-full hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {loading ? (
              <svg
                className="animate-spin w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8H4z"
                />
              </svg>
            ) : (
              "+ Create Purchase"
            )}
          </button>
          <button
            onClick={handleRemoveAll}
            className="px-5 py-2 text-sm font-semibold text-white bg-red-400 rounded-full hover:bg-red-500 transition-colors"
          >
            Remove All
          </button>
        </div>
      </div>

      {/* ── Barcode Input ── */}
      <div className="bg-white rounded-xl montserrat-font-1 border border-gray-200 shadow-sm px-5 py-4">
        <p className="text-sm font-semibold text-gray-700 mb-2 montserrat-font-2">
          Scan Barcode / Enter Item ID
        </p>
        <div className="flex gap-2 montserrat-font-1">
          <input
            ref={itemIdRef}
            type="text"
            placeholder="Scan barcode and press Enter"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                fetchItemDetails();
              }
            }}
            className="flex-1 px-4 py-2.5 text-sm border-2 border-gray-300 rounded-lg focus:outline-none focus:border-green-500 transition-colors"
          />
          <button
            onClick={fetchItemDetails}
            disabled={!itemId.trim() || loading}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1.5 montserrat-font-2">
          Same barcode scan will increase quantity automatically.
        </p>
      </div>

      {/* ── Summary Cards (always visible) ── */}
      <div className="grid grid-cols-3 gap-4 montserrat-font-1">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider montserrat-font-2">
            Rows
          </p>
          <p className="text-2xl font-bold text-gray-800 mt-1 montserrat-font-2">
            {items.length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider montserrat-font-2">
            Total Quantity
          </p>
          <p className="text-2xl font-bold text-gray-800 mt-1 montserrat-font-2">{totalQty}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider montserrat-font-2">
            Estimated Amount
          </p>
          <p className="text-2xl font-bold text-gray-800 mt-1 montserrat-font-2">
            ₹ {totalAmount.toFixed(2)}
          </p>
        </div>
      </div>

      {/* ── Items List ── */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden montserrat-font-1">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <svg
              className="w-10 h-10 mb-3 opacity-30"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M20 7H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2zM16 3H8a2 2 0 00-2 2v2h12V5a2 2 0 00-2-2z"
              />
            </svg>
            <p className="text-sm font-semibold montserrat-font-2">No items added yet</p>
            <p className="text-xs mt-1 montserrat-font-2">
              Scan an item barcode or add a new item when it is not found.
            </p>
          </div>
        ) : (
          items.map((item, index) => {
            const lineTotal =
              (parseFloat(item.purchasePrice) || 0) *
              (parseInt(item.quantity) || 0);

            return (
              <div
                key={`${item.item_id}-${index}`}
                className="flex items-center gap-4 px-5 py-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
              >
                {/* Image + Info */}
                <img
                  src={item.photo}
                  alt={item.item_name}
                  className="w-11 h-11 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                  onError={(e) => {
                    e.target.src = `https://placehold.co/44x44/e5e7eb/9ca3af?text=${item.item_name?.[0] || "?"}`;
                  }}
                />
                <div className="flex-1 min-w-0 montserrat-font-1">
                  <p className="text-sm font-semibold text-gray-800 truncate montserrat-font-2">
                    {item.item_name}
                  </p>
                  <p className="text-xs text-gray-400 montserrat-font-2">
                    Size: {item.size}&nbsp;&nbsp;ID: {item.item_id}
                  </p>
                </div>

                {/* Quantity */}
                <div className="flex flex-col items-center gap-1 montserrat-font-2">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Quantity
                  </span>
                  <div className="flex items-center gap-1 montserrat-font-1">
                    <button
                      onClick={() =>
                        handleItemChange(
                          index,
                          "quantity",
                          Math.max(1, parseInt(item.quantity) - 1),
                        )
                      }
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-300 hover:bg-gray-100 text-gray-600 font-bold text-sm transition-colors"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      className="w-14 text-center text-sm border border-gray-300 rounded-md py-1 focus:outline-none focus:ring-2 focus:ring-green-400"
                    />
                    <button
                      onClick={() =>
                        handleItemChange(
                          index,
                          "quantity",
                          parseInt(item.quantity) + 1,
                        )
                      }
                      className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-300 hover:bg-gray-100 text-gray-600 font-bold text-sm transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="flex flex-col items-center gap-1 montserrat-font-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Price
                  </span>
                  <input
                    type="number"
                    min={0}
                    placeholder="0"
                    value={item.purchasePrice}
                    onChange={(e) =>
                      handleItemChange(index, "purchasePrice", e.target.value)
                    }
                    className="w-24 text-center text-sm border border-gray-300 rounded-md py-1 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>

                {/* Line Total */}
                <div className="flex flex-col items-center gap-1 min-w-[80px] montserrat-font-1">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Line Total
                  </span>
                  <span className="text-sm font-bold text-gray-800">
                    ₹ {lineTotal.toFixed(2)}
                  </span>
                </div>

                {/* Remove */}
                <button
                  onClick={() => handleRemoveItem(index)}
                  className="px-4 py-2 text-sm font-semibold text-white bg-red-400 rounded-full hover:bg-red-500 transition-colors flex-shrink-0"
                >
                  Remove
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Not found model */}
      {showNotFoundModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white rounded-xl p-6 w-[350px] shadow-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-2 montserrat-font-1">
              Item Not Found
            </h3>
            <p className="text-sm text-gray-500 mb-5 montserrat-font-2">
              This barcode does not exist. Do you want to add a new item?
            </p>

            <div className="flex justify-end gap-3 montserrat-font-1">
              <button
                onClick={() => setShowNotFoundModal(false)}
                className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={() => {
                  setShowNotFoundModal(false);
                  setShowAddItemModal(true);
                }}
                className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 "
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddItemModal && (
        <NewPurchaseItemModel
          barcode={newBarcode}
          onClose={() => setShowAddItemModal(false)}
          onItemAdded={(newItem) => {
            setItems((prev) => {
              const existingIndex = prev.findIndex(
                (i) => String(i.item_id) === String(newItem.item_id),
              );

              if (existingIndex !== -1) {
                const updated = [...prev];
                updated[existingIndex] = {
                  ...updated[existingIndex],
                  quantity:
                    (parseInt(updated[existingIndex].quantity) || 1) + 1,
                };
                return updated;
              }

              return [
                ...prev,
                {
                  ...newItem,
                  quantity: 1,
                  purchasePrice: String(newItem.price || "0"),
                },
              ];
            });
          }}
        />
      )}

      {/* confirmation model for remove all */}

      {showClearModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
          <div className="bg-white w-[350px] rounded-xl p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-800 mb-2 montserrat-font-1">
              Clear All Items
            </h3>

            <p className="text-sm text-gray-500 mb-5 montserrat-font-2">
              Are you sure you want to remove all items?
            </p>

            <div className="flex justify-end gap-3 montserrat-font-2">
              <button
                onClick={cancelClearAll}
                className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={confirmClearAll}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default CreatePurchase;
